import Image from 'next/image'
import Link from 'next/link'
import { FunctionComponent } from 'react'
import { Route } from 'next'
import classNames from 'classnames'
import styles from './EventCard.module.css'
import { EVENT_CARD_IMAGE_HEIGHT, EVENT_CARD_IMAGE_WIDTH } from '@/config/layout.config'
import { transformEventCategory } from '@/data-access/transformers/transformEventCategory'
import { EventCardVariant } from '@/config/events.config'
import TXT from '@/messages'

type Props = {
  event: Transformed.EventForCard
  /** Where the card links to, e.g. `/santa-fe/events/jmsn` */
  href: Route
  /** The venue city shown beside the location pin */
  cityName: Nullable<string>
  variant?: EventCardVariant
}

/**
 * The event card presentation, with no dependency on client context.
 *
 * Everything context-derived (`href`, `cityName`) arrives as a prop so the card can
 * render from a server component too — `EventCard` is the client adapter that reads
 * those from `SellerProvider`.
 *
 * @param {Props} props - The card props.
 * @returns {ReactNode} The rendered event card.
 */
const EventCardView: FunctionComponent<Props> = ({
  event,
  href,
  cityName,
  variant = EventCardVariant.Default,
}: Props) => {
  const {
    id,
    title,
    url,
    category,
    text: {
      date: dateInfo,
      supportingActs,
      banner,
      ariaLabel,
    },
    image,
  } = event
  const isCompact = variant === EventCardVariant.Compact

  return (
    <Link
      href={href}
      className={classNames(styles.Wrapper, { [styles.Compact]: isCompact })}
      key={id}
      data-testid="event-card-wrapper"
      {...(isCompact ? { 'data-variant': variant } : {})}
    >
      <div
        className={styles.EventCardButton}
        aria-label={ariaLabel}
        data-testid={`event-${url}`}
        role="button"
        tabIndex={0}
      >
        {(image && (
          <Image
            aria-hidden="true"
            className={styles.Image}
            width={EVENT_CARD_IMAGE_WIDTH}
            height={EVENT_CARD_IMAGE_HEIGHT}
            src={image}
            alt={title}
            quality={100}
          />
        ))}
        {banner && (
          <div className={styles.Notification} data-testid="event-banner" title={banner}>
            {banner}
          </div>
        )}
        <div className={styles.Details}>
          <div className={styles.Title} data-testid="event-title">
            {title}
          </div>
          <div
            className={styles.Date}
            data-testid="event-date"
          >
            {dateInfo}
          </div>
          <div className={styles.GuestActs} data-testid="supporting-acts">
            {supportingActs || <>&nbsp;</>}
          </div>
          <div className={styles.Footer}>
            <div className={styles.FooterContent}>
              <div className={styles.Category} data-testid="event-category">
                <Image
                  data-testid="event-category-icon"
                  src={category === transformEventCategory('Music')
                    ? '/static/events/svg/music.svg'
                    : '/static/events/svg/community.svg'}
                  alt={category}
                  width={24}
                  height={24}
                />
                {category}
              </div>
              <div className={styles.Location} data-testid="event-location">
                <Image src="/static/events/svg/location-pin.svg" alt={TXT.eventsA11y.venueLocation(cityName)} width={24} height={24} />
                {cityName}
              </div>
            </div>
          </div>
        </div>
      </div>
    </Link>
  )
}

export default EventCardView
